// Operator sign-in (ADR-0004 requirePlatformAdmin): the logic of the old wizard's account step
// (auth.login -> POST /auth/cognito-login, 401/403 messages), restyled on the rooms components.
import { useState, type FormEvent, type ReactNode } from 'react';

import { AGENTS, AgentMessage, ChatThread } from '@/components/rooms';
import { Button, Input, Label } from '@/components/ui/shadcn';
import { Spinner } from '@/components/ui/spinner';
import { HttpError } from '@/features/onboarding/services/http-client';
import { useAuth } from '@/lib/context/auth-context';

import { OnboardingShell } from './OnboardingShell';

/** What the gate says while the operator's session is being checked. */
const LOGIN_GATE_CHECKING = 'Checking your session';

const EMAIL_PATTERN = /.+@.+\..+/;

export const isOperatorLoginValid = (email: string, password: string): boolean =>
  EMAIL_PATTERN.test(email) && password.trim().length > 0;

export function operatorErrorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    if (err.status === 403) return err.envelope?.error?.message ?? 'Only platform admins can start onboarding.';
    if (err.status === 401) return err.envelope?.error?.message ?? 'Invalid email or password.';
    return err.envelope?.error?.message ?? err.message;
  }
  if (err instanceof Error) return err.message || 'Something went wrong. Please try again.';
  return 'Something went wrong. Please try again.';
}

function LoginForm() {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const valid = isOperatorLoginValid(email, password);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await auth.login({ email, password });
    } catch (err) {
      setError(operatorErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="mt-2 w-full max-w-sm space-y-4 rounded-xl border border-border bg-card p-4">
      <div className="space-y-1.5">
        <Label htmlFor="operatorEmail">Work email</Label>
        <Input
          id="operatorEmail"
          type="email"
          autoComplete="email"
          placeholder="you@trafilea.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="operatorPassword">Password</Label>
        <Input
          id="operatorPassword"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-body-sm text-destructive">
          {error}
        </p>
      ) : null}
      <Button type="submit" className="w-full" disabled={!valid || submitting || auth.isLoading}>
        {submitting ? 'Please wait…' : 'Sign in'}
      </Button>
    </form>
  );
}

/** Renders the children only for a signed-in platform operator; otherwise the sign-in card in the rooms shell. */
export function LoginGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.isAuthenticated) return <>{children}</>;
  return (
    <OnboardingShell>
      <ChatThread>
        {auth.isLoading ? (
          <p role="status" className="flex items-center gap-2 py-1.5 text-body-sm text-ds-text-2">
            <Spinner size="sm" />
            <span className="onb-shimmer">{LOGIN_GATE_CHECKING}</span>
          </p>
        ) : (
          <AgentMessage
            agent={AGENTS.juno}
            lines={['Sign in to set up a brand.', 'Only platform admins (admin, tss-admin or auth-admin) can start onboarding.']}
          >
            <LoginForm />
          </AgentMessage>
        )}
      </ChatThread>
    </OnboardingShell>
  );
}
